import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { 
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import Button from './Button';

export interface BulkAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  variant: 'primary' | 'secondary' | 'danger' | 'success';
  requiresConfirmation?: boolean;
  confirmationTitle?: string;
  confirmationMessage?: string;
}

interface BulkActionsProps {
  selectedItems: string[];
  totalItems: number;
  actions: BulkAction[];
  onAction: (actionKey: string, selectedItems: string[]) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  isVisible: boolean;
}

const BulkActions: React.FC<BulkActionsProps> = ({
  selectedItems,
  totalItems,
  actions,
  onAction,
  onSelectAll,
  onClearSelection,
  isVisible,
}) => {
  const [confirmationAction, setConfirmationAction] = useState<BulkAction | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const handleActionClick = (action: BulkAction) => {
    if (action.requiresConfirmation) {
      setConfirmationAction(action);
      setIsConfirmationOpen(true);
    } else {
      onAction(action.key, selectedItems);
    }
  };

  const handleConfirmAction = () => {
    if (confirmationAction) {
      onAction(confirmationAction.key, selectedItems);
      setIsConfirmationOpen(false);
      setConfirmationAction(null);
    }
  };

  const handleCancelConfirmation = () => {
    setIsConfirmationOpen(false);
    setConfirmationAction(null);
  };

  if (!isVisible || selectedItems.length === 0) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-96">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-6 h-6 bg-primary-100 rounded-full">
                <CheckIcon className="w-4 h-4 text-primary-600" />
              </div>
              <span className="text-sm font-medium text-gray-900">
                {selectedItems.length} of {totalItems} selected
              </span>
            </div>
            <button
              onClick={onClearSelection}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={onSelectAll}
              className="text-xs"
            >
              Select All ({totalItems})
            </Button>

            <div className="h-4 w-px bg-gray-300" />

            {actions.map(action => (
              <Button
                key={action.key}
                variant={action.variant}
                size="sm"
                onClick={() => handleActionClick(action)}
                className="flex items-center text-xs"
              >
                {action.icon}
                <span className="ml-1">{action.label}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Transition appear show={isConfirmationOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCancelConfirmation}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="ml-3">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        {confirmationAction?.confirmationTitle || 'Confirm Action'}
                      </Dialog.Title>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-gray-500">
                      {confirmationAction?.confirmationMessage || 
                        `Are you sure you want to perform this action on ${selectedItems.length} selected items?`}
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      variant="secondary"
                      onClick={handleCancelConfirmation}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={confirmationAction?.variant || 'primary'}
                      onClick={handleConfirmAction}
                    >
                      Confirm
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default BulkActions;

// Predefined bulk actions for common use cases
export const commonBulkActions = {
  delete: {
    key: 'delete',
    label: 'Delete',
    icon: <TrashIcon className="w-4 h-4" />,
    variant: 'danger' as const,
    requiresConfirmation: true,
    confirmationTitle: 'Delete Items',
    confirmationMessage: 'Are you sure you want to delete the selected items? This action cannot be undone.',
  },
  activate: {
    key: 'activate',
    label: 'Activate',
    icon: <CheckIcon className="w-4 h-4" />,
    variant: 'success' as const,
    requiresConfirmation: true,
    confirmationTitle: 'Activate Items',
    confirmationMessage: 'Are you sure you want to activate the selected items?',
  },
  deactivate: {
    key: 'deactivate',
    label: 'Deactivate',
    icon: <XMarkIcon className="w-4 h-4" />,
    variant: 'secondary' as const,
    requiresConfirmation: true,
    confirmationTitle: 'Deactivate Items',
    confirmationMessage: 'Are you sure you want to deactivate the selected items?',
  },
  edit: {
    key: 'edit',
    label: 'Bulk Edit',
    icon: <PencilIcon className="w-4 h-4" />,
    variant: 'primary' as const,
  },
  refresh: {
    key: 'refresh',
    label: 'Refresh',
    icon: <ArrowPathIcon className="w-4 h-4" />,
    variant: 'secondary' as const,
  },
};
